import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "../environments/environment";
import { HelperService } from "./helper.service";
import { StorageKeys, StorageService } from "./storage.service";
import { UserService } from "./user.service";
import { catchError, map, switchMap, takeUntil } from "rxjs/operators";
import { forkJoin, Observable, of as observableOf, of, Subject, throwError } from "rxjs";
import { Order, ProductKeySearchAttributes, SubscriptionRepairRequest, UserGroupUpdate, UserSearchAttributes, UserUpdateAttributes } from "../models";
import { BaseContent, KillKeysResult, UserSearchCachedData } from "../models/admin.model";
import { FenderUserShopData, OrderService } from "./order.service";

@Injectable({
  providedIn: "root",
})
export class AdminService {
  public allProducts: Array<any>;
  public productMap: any = {};
  public productTypeMap: any = {};
  public queryCacheResults: any = {
    softwareKey: undefined,
    hardwareKey: undefined,
    userSearch: undefined,
    subscriptionSearch: undefined,
  };
  // Latest Fender product search kept alive across component destroy so a back-nav from
  // the detail page can restore the same result set without re-hitting the API. Cleared
  // implicitly by overwriting on the next fresh search.
  public fenderProductSearchCache: {
    queryType: string;
    queryValue: string;
    dryRunFmic: boolean;
    items: any[];
    nextToken: string | null;
    pageSize: number;
  } | null = null;
  constructor(
    private http: HttpClient,
    private _helpers: HelperService,
    private userService: UserService,
    private storage: StorageService,
    public orderService: OrderService
  ) {}

  public get(method) {
    return this.http.get(environment.paeApiUrl + `admin/${method}`, this._helpers.getHttpOptions());
  }

  public post(method, args) {
    return this.http.post(environment.paeApiUrl + `admin/${method}`, JSON.stringify(args), this._helpers.getHttpOptions());
  }

  public put(method, args) {
    return this.http.put(environment.paeApiUrl + `admin/${method}`, JSON.stringify(args), this._helpers.getHttpOptions());
  }

  public delete(method) {
    return this.http.delete(environment.paeApiUrl + `admin/${method}`, this._helpers.getHttpOptions());
  }

  public productList(type, all?, force?) {
    if (all && this.productTypeMap[type] && !force) {
      return of(this.productTypeMap[type]);
    } else {
      return this.http.get(environment.paeApiUrl + `product/list/${type}${all ? "?all=true" : ""}`, this._helpers.getHttpOptions()).pipe(
        map((result) => {
          if (all) this.productTypeMap[type] = result;
          return result;
        })
      );
    }
  }

  public searchFenderGear(sku?: string, serial?: string, dryRunFmic?: boolean) {
    let url = environment.paeApiUrl + `admin/product/fender-gear/gear-detail`;
    const params: string[] = [];
    if (sku) params.push(`sku=${sku}`);
    if (serial) params.push(`serial=${serial}`);
    if (dryRunFmic) params.push(`dryRunFmic=true`);
    if (params.length > 0) url += `?${params.join("&")}`;
    return this.http.get(url, this._helpers.getHttpOptions());
  }

  public searchFenderProducts(query: { sku?: string; productNo?: string; bodyType?: string; series?: string; title?: string; taxonomy?: string; pageSize?: number; nextToken?: string }) {
    const params: string[] = [];
    for (const key of ["sku", "productNo", "bodyType", "series", "title", "taxonomy", "pageSize", "nextToken"] as const) {
      const value = query[key];
      if (value !== undefined && value !== null && value !== "") {
        params.push(`${key}=${encodeURIComponent(String(value))}`);
      }
    }
    const qs = params.length ? `?${params.join("&")}` : "";
    return this.http.get(environment.paeApiUrl + `admin/product/fender-gear/search${qs}`, this._helpers.getHttpOptions());
  }

  public productDetail(id) {
    return this.http.get(environment.paeApiUrl + `admin/product/${id}`, this._helpers.getHttpOptions());
  }

  public productContent(productId) {
    return this.http.get(environment.paeApiUrl + `product/content/${productId}`, this._helpers.getHttpOptions());
  }

  public updateProduct(id, data) {
    return this.http.put(environment.paeApiUrl + `admin/product/${id}`, JSON.stringify(data), this._helpers.getHttpOptions());
  }

  // Promotes a product's beta build to a public release. `options`: { dryRun, releaseDate?, versionOverride?, includeDemo?, idempotencyKey? }.
  public releaseBeta(id, options) {
    return this.http.post(environment.paeApiUrl + `admin/product/${id}/release-beta`, JSON.stringify(options), this._helpers.getHttpOptions());
  }

  // Effective Release Beta config for the cosmetic frontend gate: { masterEnabled, products: { "<id>": { enabled, demoProductId? } } }.
  public getReleaseBetaConfig() {
    return this.http.get(environment.paeApiUrl + `admin/product/release-beta-config`, this._helpers.getHttpOptions());
  }

  public registerAndClaimOemKey(args) {
    return this.http.put(environment.paeApiUrl + `admin/product/oemkeys`, JSON.stringify(args), this._helpers.getHttpOptions());
  }

  public productTypes() {
    return this.http.get(environment.paeApiUrl + `product/types`, this._helpers.getHttpOptions());
  }

  public keygenProducts() {
    return this.http.get(environment.paeApiUrl + `product/key/`, this._helpers.getHttpOptions());
  }

  public generateKeys(args) {
    return this.http.post(environment.paeApiUrl + `product/key/`, JSON.stringify(args), this._helpers.getHttpOptions());
  }

  public killKeys(keys: Array<string>): Observable<KillKeysResult> {
    return this.http.post<KillKeysResult>(environment.paeApiUrl + "admin/product/kill-keys", JSON.stringify({ keys: keys }), this._helpers.getHttpOptions());
  }

  public getKeySets() {
    return this.http.get(environment.paeApiUrl + `product/keysets/`, this._helpers.getHttpOptions());
  }

  public getProductCategories() {
    return this.http.get(environment.paeApiUrl + `admin/product/categories/`, this._helpers.getHttpOptions());
  }

  public createProductCategory(args) {
    return this.http.post(environment.paeApiUrl + `admin/product/categories/`, JSON.stringify(args), this._helpers.getHttpOptions());
  }

  public updateProductCategory(args) {
    return this.http.put(environment.paeApiUrl + `admin/product/categories/${args.string_id}`, JSON.stringify(args), this._helpers.getHttpOptions());
  }

  public deleteProductCategory(stringId) {
    return this.http.delete(environment.paeApiUrl + `admin/product/categories/${stringId}`, this._helpers.getHttpOptions());
  }
  public deletePreviousVersion(id) {
    return this.http.delete(environment.paeApiUrl + `product/previous-version/${id}`, this._helpers.getHttpOptions());
  }

  public getKeySet(keySetId) {
    return this.http.get(environment.paeApiUrl + `product/download-public-key/${keySetId}`, this._helpers.getHttpOptions());
  }

  public addUpdatePreviousVersion(data) {
    let url = environment.paeApiUrl + "product/previous-version/" + (data.id ? data.id : "");
    return this.http[data.id ? "put" : "post"](url, JSON.stringify(data), this._helpers.getHttpOptions());
  }

  public addUpdateProductContent(data) {
    let url = environment.paeApiUrl + "product/content/" + (data.id ? data.id : "") + ("?productId=" + data.product_id);
    let id = data.id;
    delete data.id;
    return this.http[id ? "put" : "post"](url, JSON.stringify(data), this._helpers.getHttpOptions());
  }

  public deleteProductContentAssociation(fileName: string, stringId: string, productIds: number | number[]) {
    const productIdsParam = Array.isArray(productIds) ? productIds.join(",") : productIds;
    let url = environment.paeApiUrl + `admin/product/content-multiple/${fileName}/${stringId}?productIds=${productIdsParam}`;
    return this.http.delete(url, this._helpers.getHttpOptions());
  }

  public modifyProductContentAssociation(productIds: number[], data: BaseContent, removedProductIds?: number[]) {
    const body: any = {
      productIds: productIds,
      content: data,
    };
    if (removedProductIds?.length) {
      body.removedProductIds = removedProductIds;
    }
    return this.http.put(environment.paeApiUrl + "admin/product/content-multiple", body, this._helpers.getHttpOptions());
  }

  public sendEmail(data) {
    return this.http.post(environment.paeApiUrl + "admin/send-email", JSON.stringify(data), this._helpers.getHttpOptions());
  }

  public createJiraTicket(data: { summary: string; description: string; project?: string; type?: string; priority?: string }) {
    return this.http.post(environment.paeApiUrl + "admin/create-jira-ticket", JSON.stringify(data), this._helpers.getHttpOptions());
  }

  public addUpdateProduct(data) {
    let id = data.id || "";
    if (id) delete data.id;
    let url = environment.paeApiUrl + "admin/product/" + id;
    return this.http[id ? "put" : "post"](url, JSON.stringify(data), this._helpers.getHttpOptions());
  }

  public getProductFirmware(productId) {
    return this.http.get(environment.paeApiUrl + `product/content?productId=${productId}&contentType=firmware`, this._helpers.getHttpOptions());
  }

  public deleteProductContent(contentId, productId, firmware?: boolean) {
    let url = `product/content/${contentId}?productId=${productId}`;
    if (firmware) url += "&contentType=firmware";
    return this.http.delete(environment.paeApiUrl + url, this._helpers.getHttpOptions());
  }

  public clearProductSnapshot(productId) {
    return this.http.get(environment.paeApiUrl + `product/detail/${productId}/clear`, this._helpers.getHttpOptions());
  }

  searchUsers(searchAttributes: UserSearchAttributes) {
    return this.http.post(environment.paeApiUrl + "admin/user/search", searchAttributes, this._helpers.getHttpOptions());
  }

  searchSubscriptions(args) {
    return this.http.post(environment.paeApiUrl + "admin/subscription/search", args, this._helpers.getHttpOptions());
  }

  getRecentSubscriptions(limit = 100) {
    return this.http.get(environment.paeApiUrl + `admin/subscription/recent/${limit}`, this._helpers.getHttpOptions());
  }

  userDetail(userId: string) {
    return this.http.get(environment.paeApiUrl + `admin/user/${userId}`, this._helpers.getHttpOptions());
  }

  getUserSubscriptions(userId: string) {
    return this.http.get(environment.paeApiUrl + `admin/user/${userId}/subscription`, this._helpers.getHttpOptions());
  }

  getFenderPlaySubscription(userId: string) {
    return this.http.get(environment.paeApiUrl + `admin/user/${userId}/fender-play-subscription`, this._helpers.getHttpOptions());
  }

  createFenderPlaySubscription(userId: string, priceId: string, type: string = "gratis") {
    return this.http.post(environment.paeApiUrl + `admin/user/${userId}/fender-play-subscription/create`, { priceId, type }, this._helpers.getHttpOptions());
  }

  blessFenderPlaySubscription(userId: string, blessed: boolean) {
    return this.http.post(environment.paeApiUrl + `admin/user/${userId}/fender-play-subscription/bless`, { blessed }, this._helpers.getHttpOptions());
  }

  grantStudioPro(userId: string, productId: 1054 | 1055) {
    return this.http.post(environment.paeApiUrl + `admin/user/${userId}/grant-studio-pro`, { productId }, this._helpers.getHttpOptions());
  }

  cancelFenderPlaySubscription(userId: string, cancellationDate?: string) {
    const date = cancellationDate || new Date().toISOString().split("T")[0];
    return this.http.post(environment.paeApiUrl + `admin/user/${userId}/fender-play-subscription/cancel`, { cancellationDate: date }, this._helpers.getHttpOptions());
  }

  cancelFenderPlayRenewal(userId: string, expirationDate?: string) {
    const body = expirationDate ? { cancellationDate: expirationDate } : {};
    return this.http.post(environment.paeApiUrl + `admin/user/${userId}/fender-play-subscription/cancel`, body, this._helpers.getHttpOptions());
  }

  syncFenderPlayZuora(userId: string) {
    return this.http.post(environment.paeApiUrl + `admin/user/${userId}/fender-play-subscription/sync-zuora`, {}, this._helpers.getHttpOptions());
  }

  syncFenderPlayRecharge(userId: string, subscriptionId: string) {
    return this.http.post(environment.paeApiUrl + `admin/user/${userId}/fender-play-subscription/sync-recharge`, { subscriptionId }, this._helpers.getHttpOptions());
  }

  issueFenderPlayCredit(userId: string) {
    return this.http.post(environment.paeApiUrl + `admin/user/${userId}/fender-play-subscription/credit`, {}, this._helpers.getHttpOptions());
  }

  issueFenderPlayRefund(userId: string, paymentId: string, paymentAmount: number, paymentType: string) {
    return this.http.post(environment.paeApiUrl + `admin/user/${userId}/fender-play-subscription/refund`, { paymentId, paymentAmount, paymentType }, this._helpers.getHttpOptions());
  }

  getFenderPlayBilling(userId: string) {
    return this.http.get(environment.paeApiUrl + `admin/user/${userId}/fender-play-subscription/billing`, this._helpers.getHttpOptions());
  }

  getFenderPlayStripeDetails(userId: string) {
    return this.http.get(environment.paeApiUrl + `admin/user/${userId}/fender-play-subscription/stripe-details`, this._helpers.getHttpOptions());
  }

  getCountriesList() {
    return this.http.get(environment.paeApiUrl + `user/countries`, this._helpers.getHttpOptions());
  }

  updateUserDataAndAddress(userId: string, updateAttributes: UserUpdateAttributes) {
    return this.http.post(environment.paeApiUrl + `admin/user/${userId}`, updateAttributes, this._helpers.getHttpOptions());
  }

  deleteUserPhoto(userId: string, type: "profile" | "cover") {
    return this.http.delete(environment.paeApiUrl + `admin/user/${userId}/photo?type=${type}`, this._helpers.getHttpOptions());
  }

  /**
   * Checks whether an email address is free, across Fender Cognito, the Fender users table,
   * MySQL and the PreSonus pool. Used for live feedback in the change-email modal so an agent
   * finds out an address is taken before committing.
   */
  checkEmailAvailability(email: string) {
    return this.http.post(environment.paeApiUrl + `admin/user/check-email`, JSON.stringify({ email }), this._helpers.getHttpOptions());
  }

  /**
   * Moves a user's email everywhere it is stored and signs them out of all sessions.
   * `reason` is required and is recorded as a note on the user's profile.
   */
  changeUserEmail(userId: string, args: { newEmail: string; reason: string }) {
    return this.http.post(environment.paeApiUrl + `admin/user/${userId}/change-email`, JSON.stringify(args), this._helpers.getHttpOptions());
  }

  changeFenderCognitoPassword(email: string, password: string) {
    return this.http.post(environment.paeApiUrl + `admin/user/cognito/change-password`, { email, password }, this._helpers.getHttpOptions());
  }

  searchFenderUser(args: any) {
    return this.http.post(environment.paeApiUrl + `admin/user/search-migrate-fender-cognito-user`, JSON.stringify(args), this._helpers.getHttpOptions());
  }

  setCachedUserSearchResults(searchResults: UserSearchCachedData) {
    this.queryCacheResults.userSearch = searchResults;
  }

  public getBetas() {
    return this.http.get(environment.paeApiUrl + `admin/betas`, this._helpers.getHttpOptions());
  }

  public getBetaMembers(betaId) {
    return this.http.get(environment.paeApiUrl + `admin/betas/${betaId}/members`, this._helpers.getHttpOptions());
  }

  public removeBetaMember(betaId, userId) {
    return this.http.delete(environment.paeApiUrl + `admin/betas/${betaId}/members/${userId}`, this._helpers.getHttpOptions());
  }

  public createBeta(args) {
    return this.http.post(environment.paeApiUrl + `admin/betas`, JSON.stringify(args), this._helpers.getHttpOptions());
  }

  /**
   * Multi-community admin CRUD (DENG-728 Phase 4). List/get go through the public
   * community endpoints; create/update/delete require admin group per paeapi
   * `POST/PUT/DELETE /admin/communities`.
   */
  public createCommunity(args: any) {
    return this.http.post(environment.paeApiUrl + `admin/communities`, JSON.stringify(args), this._helpers.getHttpOptions());
  }

  public updateCommunity(communityStringId: string, args: any) {
    return this.http.put(environment.paeApiUrl + `admin/communities/${encodeURIComponent(communityStringId)}`, JSON.stringify(args), this._helpers.getHttpOptions());
  }

  public deleteCommunity(communityStringId: string) {
    return this.http.delete(environment.paeApiUrl + `admin/communities/${encodeURIComponent(communityStringId)}`, this._helpers.getHttpOptions());
  }

  /**
   * Generic auto-translate. Wraps `translateFields` (OpenAI) — any admin form that needs
   * per-locale copy from an English source can call this instead of shipping its own
   * translate endpoint. Doesn't persist — caller merges the result into whatever
   * resource it owns and saves through the resource's own PUT.
   */
  public translate(args: { fields: Record<string, string>; targetLang: string; sourceLang?: string; doNotTranslate?: string[] }) {
    return this.http.post<Record<string, Record<string, string>>>(environment.paeApiUrl + `admin/translate`, JSON.stringify(args), this._helpers.getHttpOptions());
  }

  public createCognitoUser(userId, args) {
    return this.http.post(environment.paeApiUrl + `admin/user/${userId}/create-cognito-user`, JSON.stringify(args), this._helpers.getHttpOptions());
  }

  public createFenderDynamoRecords(userId) {
    return this.http.post(environment.paeApiUrl + `admin/user/${userId}/create-fender-dynamo-records`, {}, this._helpers.getHttpOptions());
  }

  searchProductKey(searchAttributes: ProductKeySearchAttributes) {
    return this.http.post(environment.paeApiUrl + "admin/user/search-product-key", searchAttributes, this._helpers.getHttpOptions());
  }

  updateUserGroupRelation(usergroupsData: UserGroupUpdate) {
    return this.http.put(environment.paeApiUrl + `admin/user/groups`, usergroupsData, this._helpers.getHttpOptions());
  }

  getFenderUserRoles(userId: string | number) {
    return this.http.get(environment.paeApiUrl + `admin/user/${userId}/fender-roles`, this._helpers.getHttpOptions());
  }

  updateFenderUserRoles(userId: string | number, roles: Record<string, boolean>) {
    return this.http.put(environment.paeApiUrl + `admin/user/${userId}/fender-roles`, roles, this._helpers.getHttpOptions());
  }

  public cachedGroups: any;
  getGroups(force?: boolean) {
    if (!force && this.cachedGroups) return of(this.cachedGroups);
    return this.http.get(environment.paeApiUrl + `admin/groups`, this._helpers.getHttpOptions()).pipe(
      map((result) => {
        this.cachedGroups = result;
        return result;
      })
    );
  }

  updateGroup(groupId: string, data: any) {
    return this.http.put(environment.paeApiUrl + `admin/groups/${groupId}`, JSON.stringify(data), this._helpers.getHttpOptions());
  }

  createGroup(data: any) {
    return this.http.post(environment.paeApiUrl + `admin/groups`, JSON.stringify(data), this._helpers.getHttpOptions());
  }

  getGroupDetails(groupId: string) {
    return this.http.get(environment.paeApiUrl + `admin/groups/${groupId}`, this._helpers.getHttpOptions());
  }

  deleteUser(userId: string, softDelete: boolean) {
    return this.http.delete(environment.paeApiUrl + `admin/user/${userId}?softDelete=${softDelete}`, this._helpers.getHttpOptions());
  }

  updateExtendedSupportDate(userId: number, date: string, assetId: number) {
    let url = environment.paeApiUrl + "admin/user/" + userId + "/asset/" + assetId + "/update-extended-date";
    let data = { extendedDate: date };
    return this.http.put(url, JSON.stringify(data), this._helpers.getHttpOptions());
  }
  deregisterHardwareKeys(keys: string[]) {
    return this.http.post(environment.paeApiUrl + `admin/user/deregister-serial-numbers`, keys, this._helpers.getHttpOptions());
  }

  removeHardware(userId: any, licenseId: any) {
    return this.http.delete(environment.paeApiUrl + `admin/user/${userId}/license/${licenseId}`, this._helpers.getHttpOptions());
  }

  clearUserCache(userId: string) {
    return this.http.get(environment.paeApiUrl + `admin/user/${userId}/clear-cache`, this._helpers.getHttpOptions());
  }

  public cancelSubscription(userId: string, subscriptionId: string, expireImmediately?: boolean, clear?: boolean) {
    return this.http.get(
      environment.paeApiUrl +
        `admin/user/${userId}/cancel-subscription/${subscriptionId}${expireImmediately || clear ? `?expire=${expireImmediately ? "true" : "false"}${clear ? "&clear=true" : ""}` : ""}`,
      this._helpers.getHttpOptions()
    );
  }

  public userProductsList: any = {};
  getProductsList(userId: string, type: "software" | "hardware" | "firmware" | "extensions" | "subscription", force?: boolean) {
    if (this.userProductsList[userId]?.[type] && !force) return of(this.userProductsList[userId][type]);
    return this.http.get(environment.paeApiUrl + `admin/user/${userId}/products/${type}/list`, this._helpers.getHttpOptions()).pipe(
      map((result) => {
        if (!this.userProductsList[userId]) this.userProductsList[userId] = {};
        this.userProductsList[userId][type] = result;
        return result;
      })
    );
  }

  public repairSubscription(subscriptionDetails: SubscriptionRepairRequest) {
    return this.http.post(environment.paeApiUrl + `admin/subscription/${subscriptionDetails.subscriptionId}/repair`, subscriptionDetails, this._helpers.getHttpOptions());
  }

  public translateString(text, language) {
    const apiKey = environment.googleTranslateKey; // replace with your actual API key
    const url = `https://translation.googleapis.com/language/translate/v2`;
    const body = {
      q: text,
      target: language,
      format: "text",
    };

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    return this.http.post(`${url}?key=${apiKey}`, body, { headers });
  }

  public userComments: any;
  public getUserComments(userId: string, force?: boolean) {
    if (this.userComments && !force) return of(this.userComments);
    return this.http.get(environment.paeApiUrl + `admin/user/${userId}/comments`, this._helpers.getHttpOptions()).pipe(
      map((result) => {
        this.userComments = result;
        return result;
      })
    );
  }

  public saveUserComments(userId: string, body: { comment: string }) {
    return this.http.post(environment.paeApiUrl + `admin/user/${userId}/comments`, body, this._helpers.getHttpOptions());
  }

  public modifyUserComments(userId: string, commentId: string, body: { comment: string }) {
    return this.http.put(environment.paeApiUrl + `admin/user/${userId}/comments/${commentId}`, body, this._helpers.getHttpOptions());
  }

  public deleteUserComments(userId: string, commentId: string) {
    return this.http.delete(environment.paeApiUrl + `admin/user/${userId}/comments/${commentId}`, this._helpers.getHttpOptions());
  }

  public transferSoftwareLicense({ licenseId, toEmail, fromUserId }) {
    return this.http.post(environment.paeApiUrl + `admin/user/transfer-license`, { licenseId, toEmail, fromUserId }, this._helpers.getHttpOptions());
  }

  public transferAllAssets({ toEmail, fromUserId }) {
    return this.http.post(environment.paeApiUrl + `admin/user/transfer-all-assets`, { toEmail, fromUserId }, this._helpers.getHttpOptions());
  }

  public resetSoftwareActivations({ id, licenseId }) {
    return this.http.post(environment.paeApiUrl + `admin/user/${id}/reset-software-license/${licenseId}`, null, this._helpers.getHttpOptions());
  }

  public resetHardwareActivations({ id, hwSerialNumber }) {
    return this.http.delete(environment.paeApiUrl + `admin/user/${id}/delete-hardware-activations/${hwSerialNumber}`, this._helpers.getHttpOptions());
  }

  public resetSoftwareKey({ licenseId, productId }) {
    return this.http.get(environment.paeApiUrl + `admin/product/${productId}/reset-license-key/${licenseId}`, this._helpers.getHttpOptions());
  }

  public getAiAssistantConfigs() {
    return this.http.get(environment.paeApiUrl + "admin/ai_assistant/configs", this._helpers.getHttpOptions());
  }

  public getAiAssistantConfig(clientId: string, model: string, requestType: string) {
    return this.http.get(environment.paeApiUrl + `admin/ai_assistant/config/${clientId}/${model}?request_type=${requestType}`, this._helpers.getHttpOptions());
  }

  public saveAiAssistantConfig(config: any) {
    return this.http.post(environment.paeApiUrl + "admin/ai_assistant/config", JSON.stringify(config), this._helpers.getHttpOptions());
  }

  public updateAiAssistantConfig(clientId: string, model: string, config: any) {
    return this.http.put(environment.paeApiUrl + `admin/ai_assistant/config/${clientId}/${model}`, JSON.stringify(config), this._helpers.getHttpOptions());
  }

  public deleteAiAssistantConfig(clientId: string, model: string, requestType: string) {
    return this.http.delete(environment.paeApiUrl + `admin/ai_assistant/config/${clientId}/${model}?request_type=${requestType}`, this._helpers.getHttpOptions());
  }

  // Monthly AI Assistant usage rollup. Months are YYYY-MM (UTC); omitting both bounds
  // returns the trailing 12 months.
  public getAiAssistantUsage(start?: string, end?: string) {
    const params = [start ? `start=${start}` : "", end ? `end=${end}` : ""].filter((part) => !!part).join("&");
    return this.http.get(environment.paeApiUrl + `admin/ai_assistant/usage${params ? "?" + params : ""}`, this._helpers.getHttpOptions());
  }

  public swapSoftwareLicense({ userId, licenseId, oldKey, finalKey }) {
    return this.http.post(environment.paeApiUrl + `admin/user/${userId}/swap-software-license`, { licenseId, oldKey, finalKey }, this._helpers.getHttpOptions());
  }

  public removeSoftwareLicense({ userId, productId, licenseId }) {
    return this.http.post(environment.paeApiUrl + `admin/user/${userId}/remove-software-license/${productId}/${licenseId}`, null, this._helpers.getHttpOptions());
  }

  public removeSoftwareLicenseWithRelatedItems({ userId, productId, licenseId }) {
    return this.http.post(environment.paeApiUrl + `admin/user/${userId}/remove-delete-software/${productId}/${licenseId}`, null, this._helpers.getHttpOptions());
  }

  public getProductHistory({ userId, licenseId }) {
    return this.http.get(environment.paeApiUrl + `admin/user/${userId}/history/${licenseId}`, this._helpers.getHttpOptions());
  }

  public impersonateUser(userId: string) {
    return this.http.get(environment.paeApiUrl + `admin/user/${userId}/impersonate`, this._helpers.getHttpOptions());
  }

  public productRegistration(registrationRequest: { userId: string; productkey?: string; type: string; serial_number?: string; purchase_date?: string; force?: boolean; hardware_id?: string }) {
    return this.http.post(environment.paeApiUrl + `admin/user/${registrationRequest.userId}/register-product`, registrationRequest, this._helpers.getHttpOptions());
  }

  /**
   * Force-registers a Fender product by SKU, bypassing serial verification entirely. Used by
   * support when a serial can't be validated through the normal registration path (guitars,
   * Tone Master Pro, replacement units) — the SKU comes from the FMIC product search.
   */
  public forceRegisterFenderProduct(registrationRequest: { userId: string; sku: string; serial_number: string; purchase_date?: string }) {
    return this.http.post(environment.paeApiUrl + `admin/user/${registrationRequest.userId}/force-register-fender-product`, JSON.stringify(registrationRequest), this._helpers.getHttpOptions());
  }

  public registerMultipleHardware(registrationRequest: { serials: Array<string>; userId: string; hardwareId?: string; purchaseDate?: string; forceRegistration?: boolean }) {
    return this.http.post(environment.paeApiUrl + `admin/user/${registrationRequest.userId}/register-multiple-hardware`, registrationRequest, this._helpers.getHttpOptions());
  }

  public productAssociation(data) {
    return this.http.post(environment.paeApiUrl + "admin/content/product-association", JSON.stringify(data), this._helpers.getHttpOptions());
  }

  public updateProductFlagship(data) {
    return this.http.post(environment.paeApiUrl + "admin/content/update-product-flagship", JSON.stringify(data), this._helpers.getHttpOptions());
  }

  public getUserOrders(userId: string) {
    return this.http.get(environment.paeApiUrl + `admin/user/${userId}/order/list`, this._helpers.getHttpOptions());
  }

  public ordersById: any = {};
  getAllUserOrders(email: string, userId: any, siteID?: string): Observable<Order[]> {
    return this.orderService.getFenderShopifyAccountDetails(email, true, siteID).pipe(
      switchMap((accountDetails: FenderUserShopData) => {
        if (!accountDetails?.shopifyId) {
          // If no Shopify account exists, just return regular orders
          return this.getUserOrders(userId).pipe(map((orders) => this.orderService.transformPresonusOrderData(orders as any[])));
        }

        // Call both order methods in parallel
        return forkJoin({
          orders: this.getUserOrders(userId),
          fenderShopifyOrders: this.orderService.getFenderShopifyOrders(accountDetails.shopifyId, accountDetails.storeId),
        }).pipe(
          map((result) => {
            const presonusOrders = this.orderService.transformPresonusOrderData(result.orders as any[]);
            const fenderOrders = this.orderService.transformFenderOrderData(result.fenderShopifyOrders);
            // Combine both arrays into one
            return [...presonusOrders, ...fenderOrders];
          })
        );
      })
    );
  }

  public getUserTickets(userId: string) {
    return this.http.get(environment.paeApiUrl + `admin/user/${userId}/ticket`, this._helpers.getHttpOptions());
  }

  public clearHardwareSerialAndDeleteSubscription(serialNumber: string) {
    return this.http.put(environment.paeApiUrl + `admin/product/hardware-serial/${serialNumber}`, null, this._helpers.getHttpOptions());
  }

  public fixMixedCaseEmail(userId: string) {
    return this.http.get(environment.paeApiUrl + `admin/user/${userId}/fix-email`, this._helpers.getHttpOptions());
  }

  public deleteSoftwareActivation(params: { userId: string; licenseId: string; activationId?: string }) {
    const data: any = { licenseId: params.licenseId };
    if (params.activationId) {
      data.activationId = params.activationId;
    }
    return this.http.post(environment.paeApiUrl + `admin/user/${params.userId}/delete-software-activation`, data, this._helpers.getHttpOptions());
  }

  /**
   * Get product names from product IDs with caching support
   * @param productIds Array of product IDs (strings or numbers)
   * @param destroy$ Subject for unsubscribing
   * @returns Observable that emits array of product names
   */
  public getProductNames(productIds: (string | number)[], destroy$: any): Observable<string[]> {
    const ids = productIds.map((id) => parseInt(id as any, 10)).filter((n) => !isNaN(n));

    if (ids.length === 0) {
      return of([]);
    }

    // First try to get from cached product map
    const cachedProducts = ids.map((id) => this.productMap[id]).filter((p) => !!p);

    if (cachedProducts.length === ids.length) {
      // All products found in cache
      const productNames = cachedProducts.map((p: any) => p.title);
      return of(productNames);
    } else {
      // Fetch missing product details
      const requests = ids.map((id) => this.productDetail(id));
      return forkJoin(requests).pipe(
        takeUntil(destroy$),
        map((results: any[]) => {
          const fetched = results.map((r) => (r?.snapshot?.product ? r.snapshot.product : r)).filter(Boolean);
          return fetched.map((p: any) => p.title);
        })
      );
    }
  }

  /**
   * Get product info objects from product IDs with caching support
   * @param productIds Array of product IDs (strings or numbers)
   * @param destroy$ Subject for unsubscribing
   * @returns Observable that emits array of product info objects
   */
  public getProductInfo(productIds: (string | number)[], destroy$: any): Observable<Array<{ id: number; title: string; imageUrl?: string }>> {
    const ids = productIds.map((id) => parseInt(id as any, 10)).filter((n) => !isNaN(n));

    if (ids.length === 0) {
      return of([]);
    }

    // First try to get from cached product map
    const cachedProducts = ids.map((id) => this.productMap[id]).filter((p) => !!p);

    if (cachedProducts.length === ids.length) {
      // All products found in cache
      const productInfo = cachedProducts.map((p: any) => ({
        id: p.id,
        title: p.title,
        imageUrl: p.imageUrl,
      }));
      return of(productInfo);
    } else {
      // Fetch missing product details
      const requests = ids.map((id) => this.productDetail(id));
      return forkJoin(requests).pipe(
        takeUntil(destroy$),
        map((results: any[]) => {
          const fetched = results.map((r) => (r?.snapshot?.product ? r.snapshot.product : r)).filter(Boolean);
          return fetched.map((p: any) => ({
            id: p.id,
            title: p.title,
            imageUrl: p.imageUrl,
          }));
        })
      );
    }
  }

  public requestGdprBulkUploadUrl() {
    return this.http.post<{ uploadUrl: string; filename: string }>(environment.paeApiUrl + "admin/gdpr-bulk-delete", {}, this._helpers.getHttpOptions());
  }

  public requestBulkUserGrantUploadUrl() {
    return this.http.post<{ uploadUrl: string; filename: string }>(environment.paeApiUrl + "admin/bulk-user-grant-upload-url", {}, this._helpers.getHttpOptions());
  }

  /**
   * Queues a bulk user/grant import. Returns as soon as the rows are enqueued — poll
   * getBulkUserGrantJob with the returned jobId for progress and per-row results.
   */
  public processBulkUserGrant(filename: string) {
    return this.http.post<{ jobId: string; total: number; queued: number; invalid: number }>(environment.paeApiUrl + "admin/bulk-user-grant/process", { filename }, this._helpers.getHttpOptions());
  }

  public getBulkUserGrantJob(jobId: string) {
    return this.http.get<{ jobId: string; status: "processing" | "complete"; total: number; completed: number; results: any[] }>(
      environment.paeApiUrl + "admin/bulk-user-grant/job/" + jobId,
      this._helpers.getHttpOptions()
    );
  }

  /** Fender Play paths, read from Contentful by the API (id, title, slug, level count). */
  public getPlayPaths(refresh = false) {
    return this.http.get<{ paths: { id: string; title: string; slug: string; levelCount: number }[] }>(
      environment.paeApiUrl + "admin/play-paths" + (refresh ? "?refresh=true" : ""),
      this._helpers.getHttpOptions()
    );
  }
}
